import { useEffect, useRef } from 'react';
import { useNotifications, useStore } from '../store/useStore';
import { leadService } from '../lib/leadService';
import { supabase } from '../lib/supabase';
import { Lead, LEAD_SOURCE_LABELS } from '../types/leads.types';
import { APP_ICON } from '../lib/brand';

const AUDIO_PATH = '/sounds/reminder.mp3';
/** Il form_name dei lead creati dalla lettura degli ordini (ordini-sito/ordine.ts). */
const NOME_MODULO_ACQUISTO_BIGLIETTI = 'Acquisto biglietti sito';
const POLL_INTERVAL_MS = 60 * 1000; // controlla i nuovi lead ogni minuto

// Allarme audio + notifica + pallino quando arriva un NUOVO lead nella tabella
// (da qualsiasi sorgente: sito, meta, google, whatsapp...). Polling client-side:
// funziona finché l'app è aperta in una scheda del browser.
export const useNewLeadAlerts = () => {
    const { addNotification } = useNotifications();
    const incrementNewLeadCount = useStore((s) => s.incrementNewLeadCount);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    // Baseline: consideriamo "nuovi" solo i lead creati dopo l'apertura dell'app.
    const sinceRef = useRef<string>(new Date().toISOString());
    const myIdRef = useRef<string | null>(null);

    useEffect(() => {
        const audio = new Audio(AUDIO_PATH);
        audio.preload = 'auto';
        audioRef.current = audio;
    }, []);

    // Sblocca l'audio al primo gesto utente (autoplay policy del browser)
    useEffect(() => {
        const unlock = () => {
            const audio = audioRef.current;
            if (!audio) return;
            audio.play().then(() => { audio.pause(); audio.currentTime = 0; }).catch(() => { /* riprova al trigger */ });
        };
        window.addEventListener('pointerdown', unlock, { once: true });
        window.addEventListener('keydown', unlock, { once: true });
        return () => {
            window.removeEventListener('pointerdown', unlock);
            window.removeEventListener('keydown', unlock);
        };
    }, []);

    // ID utente corrente: non suoniamo per i lead creati manualmente da me.
    useEffect(() => {
        supabase.auth.getUser()
            .then(({ data }) => { myIdRef.current = data.user?.id ?? null; })
            .catch(() => { /* ignorato */ });
    }, []);

    useEffect(() => {
        let cancelled = false;

        const fire = (lead: Lead) => {
            const name = lead.full_name || lead.company_name || 'Nuovo lead';
            const sourceLabel = LEAD_SOURCE_LABELS[lead.source] || lead.source || '';
            const message = sourceLabel ? `${name} • ${sourceLabel}` : name;

            if (audioRef.current) {
                audioRef.current.currentTime = 0;
                audioRef.current.play().catch(() => { /* gesto utente richiesto */ });
            }
            if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
                new Notification('Nuovo lead', { body: message, icon: APP_ICON });
            }
            addNotification({ type: 'success', title: 'Nuovo lead', message });
        };

        const check = async () => {
            try {
                const fresh = await leadService.getLeadsCreatedAfter(sinceRef.current);
                if (cancelled || fresh.length === 0) return;
                // avanza la baseline all'ultimo created_at visto (niente doppioni)
                sinceRef.current = fresh[fresh.length - 1].created_at;
                // salta i lead creati manualmente da me stesso, e quelli di chi
                // compra biglietti sul sito: per loro suona l'avviso di Eventi
                // (useBigliettiAlerts), due suoni per lo stesso acquisto no.
                const alertable = fresh.filter(
                    (l) => l.created_by !== myIdRef.current && l.form_name !== NOME_MODULO_ACQUISTO_BIGLIETTI,
                );
                if (alertable.length === 0) return;
                incrementNewLeadCount(alertable.length);
                alertable.forEach(fire);
            } catch {
                /* non bloccante */
            }
        };

        const intervalId = window.setInterval(check, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [addNotification, incrementNewLeadCount]);
};

export default useNewLeadAlerts;
